import React from 'react';

/**
 * LoadingShim - tiny fallback UIs used while React.lazy() chunks download.
 *
 * `ModalLoadingShim`: centered spinner on a translucent black backdrop.
 *   Used as the <Suspense fallback> around lazy-loaded modal mount sites.
 *
 * `PageLoadingShim`: full-bleed centered spinner matching the dark chrome.
 *   Used as the <Suspense fallback> around lazy-loaded page routes so the
 *   app chrome (nav bars) doesn't flash during a route-level chunk fetch.
 *
 * Intentionally styled inline (no Tailwind utility classes beyond a couple)
 * so these render correctly even before the main CSS chunk has parsed.
 */

const spinnerKeyframes = `
@keyframes qm-shim-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
`;

const Spinner = ({ size = 36, color = 'var(--qm-accent)' }: { size?: number; color?: string }) => (
    <div
        role="status"
        aria-label="Loading"
        style={{
            width: size,
            height: size,
            border: `3px solid rgba(255,255,255,0.12)`,
            borderTopColor: color,
            borderRadius: '50%',
            animation: 'qm-shim-spin 0.8s linear infinite',
        }}
    />
);

export const ModalLoadingShim = () => (
    <div
        style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            backdropFilter: 'blur(2px)',
        }}
        aria-busy="true"
        aria-live="polite"
    >
        <style>{spinnerKeyframes}</style>
        <Spinner size={40} />
    </div>
);

export const PageLoadingShim = () => (
    <div
        style={{
            position: 'absolute',
            inset: 0,
            background: '#0f0f10',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}
        aria-busy="true"
        aria-live="polite"
    >
        <style>{spinnerKeyframes}</style>
        <Spinner size={36} />
    </div>
);

export default ModalLoadingShim;
